import { useCallback, useEffect, useRef, useState } from "react";
import { StickyNote, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ukDateTime } from "./crmPipelineShared";
import { LoadError, errMsg } from "./LoadError";

const TYPES = ["Note", "Phone call", "Email", "Meeting", "Follow-up instruction"];
const inputCls = "w-full bg-[#091E16] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-brass";

function NoteItem({ n, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(n.content);
  const [type, setType] = useState(n.note_type);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!text.trim()) return toast.error("Note cannot be empty");
    setBusy(true);
    try { await api.put(`/crm/notes/${n.id}`, { content: text, note_type: type }); toast.success("Note updated"); setEditing(false); onChanged(); }
    catch (e) { toast.error(errMsg(e, "Could not update note")); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this note? This cannot be undone from the record.")) return;
    setBusy(true);
    try { await api.delete(`/crm/notes/${n.id}`); toast.success("Note deleted"); onChanged(); }
    catch (e) { toast.error(errMsg(e, "Could not delete note")); setBusy(false); }
  };

  return (
    <li className="bg-white/[0.03] border border-white/5 rounded-lg p-3" data-testid={`note-${n.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-brass/15 text-brass">{n.note_type}</span>
        {!editing && (
          <div className="flex gap-1">
            <button onClick={() => setEditing(true)} data-testid={`note-edit-${n.id}`} className="p-1 text-emerald-100/50 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
            <button onClick={remove} disabled={busy} data-testid={`note-delete-${n.id}`} className="p-1 text-emerald-100/50 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="mt-2 space-y-2">
          <select value={type} onChange={(e) => setType(e.target.value)} className={inputCls} data-testid={`note-edit-type-${n.id}`}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} className={inputCls} data-testid={`note-edit-text-${n.id}`} />
          <div className="flex gap-2">
            <button onClick={save} disabled={busy} data-testid={`note-edit-save-${n.id}`} className="text-xs bg-brass text-emerald-950 font-semibold rounded-lg px-3 py-1.5">{busy ? "Saving…" : "Save"}</button>
            <button onClick={() => { setEditing(false); setText(n.content); setType(n.note_type); }} className="text-xs text-emerald-100/60 px-2">Cancel</button>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-sm text-white whitespace-pre-wrap break-words">{n.content}</p>
      )}
      <div className="mt-1.5 text-[11px] text-emerald-100/40">
        {ukDateTime(n.created_at)}{n.created_by ? ` · ${n.created_by}` : ""}{n.edited_at ? ` · edited ${ukDateTime(n.edited_at)}` : ""}
      </div>
    </li>
  );
}

export function NotesPanel({ recordType, recordId, onAdded, readOnly = false }) {
  const draftKey = `ec_note_draft_${recordType}_${recordId}`;
  const [notes, setNotes] = useState(null);
  const [error, setError] = useState(false);
  const [text, setText] = useState(() => localStorage.getItem(draftKey) || "");
  const [type, setType] = useState("Note");
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    setError(false);
    try { const { data } = await api.get("/crm/notes", { params: { record_type: recordType, record_id: recordId } }); setNotes(data.notes || []); }
    catch (e) { setError(true); }
  }, [recordType, recordId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (text) localStorage.setItem(draftKey, text); else localStorage.removeItem(draftKey); }, [text, draftKey]);

  const add = async () => {
    if (inFlight.current) return;
    if (!text.trim()) return toast.error("Write a note first");
    inFlight.current = true; setBusy(true);
    try {
      await api.post("/crm/notes", { record_type: recordType, record_id: recordId, note_type: type, content: text });
      toast.success("Note saved"); setText(""); setType("Note"); load(); onAdded && onAdded();
    } catch (e) { toast.error(errMsg(e, "Could not save note — your text is kept, please retry")); }
    finally { inFlight.current = false; setBusy(false); }
  };

  return (
    <div className="bg-[#0F2E23] rounded-xl border border-white/10 p-5" data-testid="notes-panel">
      <div className="flex items-center gap-2 mb-3"><StickyNote className="w-4 h-4 text-brass" /><h3 className="text-sm font-semibold text-white">Notes</h3>{notes && <span className="text-[11px] text-emerald-100/40">({notes.length})</span>}</div>
      {!readOnly && (
        <div className="space-y-2 mb-4">
          <div className="flex gap-2 flex-wrap">
            {TYPES.map((t) => (
              <button key={t} onClick={() => setType(t)} data-testid={`note-type-${t.replace(/\s/g, "-").toLowerCase()}`}
                className={`text-[11px] rounded-full px-2.5 py-1 border ${type === t ? "bg-brass text-emerald-950 border-brass font-semibold" : "border-white/10 text-emerald-100/70 hover:bg-white/5"}`}>{t}</button>
            ))}
          </div>
          <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="Record a call, email, meeting outcome or follow-up instruction…" className={inputCls} data-testid="note-input" />
          <button onClick={add} disabled={busy} data-testid="note-save" className="inline-flex items-center gap-1.5 text-sm bg-brass hover:brightness-110 text-emerald-950 font-semibold rounded-lg px-4 py-1.5 disabled:opacity-60">
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{busy ? "Saving…" : "Add note"}
          </button>
        </div>
      )}
      {error && <LoadError message="Notes could not be loaded." onRetry={load} testId="notes-error" />}
      {!error && notes === null && <p className="text-xs text-emerald-100/40">Loading notes…</p>}
      {notes && notes.length === 0 && <p className="text-xs text-emerald-100/40" data-testid="notes-empty">No notes yet.</p>}
      {notes && notes.length > 0 && <ol className="space-y-2.5" data-testid="notes-list">{notes.map((n) => <NoteItem key={n.id} n={n} onChanged={load} />)}</ol>}
    </div>
  );
}
